import React, { PureComponent } from 'react';
import cn from 'classnames';
import { getDeviceType } from '@platform-ui/device-info';
import { PopoverContext } from '@platform-ui/popover';
import { PortalWrapper } from '@platform-ui/portal';
import { dataQa, extractLayer, pickStyle } from '@tui-react/core';
import OffsetWrapper from './OffsetWrapper';
import ScrollBar from './ScrollBar';
import { ScrollContent } from './ScrollContent';
import { stopOuterScroll } from './utils';
import classes from './Scroll.module.css';
const BAR_OFFSET = 15;
const MIN_BAR_HEIGHT = 17;
const FADE_TIMEOUT = 300;
const touchMoveOptions = {
  passive: false
};
const BUFFER = 1; // buffer for rounding issues

/* eslint-disable no-param-reassign */
const setTransform = ({
  el,
  v,
  offset
}) => {
  const value = `translate${v}(${offset}px)`;
  el.style.transform = value;
  el.style['MozTransform'] = value;
  el.style['OTransform'] = value;
  el.style['MsTransform'] = value;
  el.style['WebkitTransform'] = value;
};
/* eslint-enable no-param-reassign */
const setBarMinHeight = barMinHeight => barMinHeight > MIN_BAR_HEIGHT ? barMinHeight : MIN_BAR_HEIGHT;
export const SCROLLBAR_VISIBILITY = {
  ALWAYS: 'always',
  ONLY_ACTIVE: 'onlyActive',
  HIDDEN: 'hidden'
};
class Scroll extends PureComponent {
  constructor(_props) {
    super(_props);
    this.content = void 0;
    this.container = void 0;
    this.verticalBar = void 0;
    this.horizontalBar = void 0;
    this.vTimeoutId = void 0;
    this.hTimeoutId = void 0;
    this.scrollingId = void 0;
    this.startOffset = void 0;
    this.isUnmounting = void 0;
    this.resizeObserver = void 0;
    this.mutationObserver = void 0;
    this.direction = void 0;
    this.mobile = getDeviceType() !== 'desktop';
    this.barMinHeight = setBarMinHeight(this.props.barMinHeight);
    this.horizontalProportion = 1;
    this.verticalProportion = 1;
    this.currentY = 0;
    this.currentX = 0;
    this.horizontalOffset = 0;
    this.verticalOffset = 0;
    this.getScrollBarVisibility = direction => {
      const {
        scrollbarVisibility: scrollbarVisibilityProp,
        fallback
      } = this.props;

      /**
       * Если мобила и проп fallback не передан,
       * то ожидается нативный скроллбар,
       * который виден только когда активен.
       * Поэтому игнорируем проп scrollbarVisibility.
       */
      const scrollbarVisibility = this.mobile && fallback === undefined ? SCROLLBAR_VISIBILITY.ONLY_ACTIVE : scrollbarVisibilityProp;
      switch (scrollbarVisibility) {
        case SCROLLBAR_VISIBILITY.ONLY_ACTIVE:
          return this.state.hovered || this.state.scrolling || (direction === 'vertical' ? this.state.isVScrolling || this.state.isVDragging : this.state.isHScrolling || this.state.isHDragging);
        case SCROLLBAR_VISIBILITY.HIDDEN:
          return false;
        default:
          return true;
      }
    };
    this.setRef = ref => element => {
      this[ref] = element;
    };
    this.contentRef = node => {
      var _this$props$setContai, _this$props;
      (_this$props$setContai = (_this$props = this.props).setContainer) == null || _this$props$setContai.call(_this$props, node);
      this.content = node;
    };
    this.setCurrentCursorCoords = event => {
      this.currentY = event.touches[0].clientY;
      this.currentX = event.touches[0].clientX;
    };
    this.stopOuterScrollDecorator = event => {
      stopOuterScroll(event, this.currentY, this.currentX);
    };
    this.setListeners = () => {
      if (this.content) {
        this.content.addEventListener('wheel', this.stopOuterScrollDecorator);
        this.content.addEventListener('touchstart', this.setCurrentCursorCoords);
        this.content.addEventListener('touchmove', this.stopOuterScrollDecorator, touchMoveOptions);
      }
    };
    this.removeListeners = () => {
      if (this.content) {
        this.content.removeEventListener('wheel', this.stopOuterScrollDecorator);
        this.content.removeEventListener('touchstart', this.setCurrentCursorCoords);
        this.content.removeEventListener('touchmove', this.stopOuterScrollDecorator, touchMoveOptions);
      }
    };
    this.updateFade = ({
      scrollTop,
      scrollHeight,
      clientHeight
    }) => {
      const {
        verticalFade
      } = this.props;
      if (!verticalFade || !this.container) return;
      this.container.setAttribute('data-vertical-fade-start', `${!!scrollTop}`);
      this.container.setAttribute('data-vertical-fade-end', `${!!(Math.round(scrollTop) < scrollHeight - clientHeight - BUFFER)}`);
    };
    this.onDragStart = () => {
      if (!this.content) {
        return;
      }
      switch (this.direction) {
        case 'horizontal':
          this.startOffset = this.content.scrollLeft * this.horizontalProportion;
          break;
        case 'vertical':
          this.startOffset = this.content.scrollTop * this.verticalProportion;
          break;
      }
    };
    this.onDragProcess = (current, prev, start) => {
      if (!this.content) {
        return;
      }
      switch (this.direction) {
        case 'horizontal':
          this.content.scrollLeft = ((this.startOffset || 0) + current.x - start.x) / this.horizontalProportion;
          break;
        case 'vertical':
          this.content.scrollTop = ((this.startOffset || 0) + current.y - start.y) / this.verticalProportion;
          break;
      }
    };
    this.onDragEnd = () => {
      this.direction = undefined;
      this.setDraggingDirection();
    };
    // eslint-disable-next-line max-statements
    this.handleScroll = event => {
      const {
        verticalBarRef,
        horizontalBarRef,
        contentInnerOffsetTop,
        contentInnerOffsetBottom,
        onScroll,
        barMaxHeight
      } = this.props;
      if (!this.content || !this.container) {
        return;
      }
      const {
        scrollTop,
        scrollLeft,
        scrollWidth,
        scrollHeight,
        clientWidth,
        clientHeight
      } = this.content;
      const verticalBar = verticalBarRef || this.verticalBar;
      const horizontalBar = horizontalBarRef || this.horizontalBar;
      this.horizontalOffset = scrollLeft * this.horizontalProportion;
      const verticalBarProportion = verticalBar && verticalBar.parentElement ? verticalBar.parentElement.clientHeight / clientHeight : 1;
      const horizontalBarProportion = horizontalBar && horizontalBar.parentElement ? horizontalBar.parentElement.clientWidth / clientWidth : 1;
      const offsetScaleRatio = (clientHeight + contentInnerOffsetTop + contentInnerOffsetBottom) / clientHeight;
      const baseVerticalSize = (clientHeight / scrollHeight * this.container.clientHeight - BAR_OFFSET * +(this.horizontalProportion < 1)) * verticalBarProportion;
      const scrollPosition = scrollTop * this.verticalProportion * offsetScaleRatio * verticalBarProportion;
      let verticalSize = baseVerticalSize;
      if (this.barMinHeight) {
        verticalSize = Math.max(baseVerticalSize, this.barMinHeight);
      }
      if (barMaxHeight) {
        verticalSize = Math.min(verticalSize, barMaxHeight);
      }
      if (verticalSize !== baseVerticalSize) {
        const diffHeight = -verticalSize + baseVerticalSize;
        const scrollPositionToAvailableScrollRatio = scrollTop / (scrollHeight - this.container.clientHeight);
        this.verticalOffset = scrollPosition + diffHeight * scrollPositionToAvailableScrollRatio;
      } else {
        this.verticalOffset = scrollPosition;
      }
      if (this.horizontalOffset !== this.state.horizontalOffset && horizontalBar) {
        this.setState({
          isHScrolling: true,
          horizontalOffset: this.horizontalOffset
        }, this.onStateUpdate);
        this.setScrollingTimeout('horizontal');
        setTransform({
          el: horizontalBar,
          v: 'X',
          offset: this.horizontalOffset
        });
      }
      if (this.verticalOffset !== this.state.verticalOffset && verticalBar) {
        this.setState({
          isVScrolling: true,
          verticalOffset: this.verticalOffset
        }, this.onStateUpdate);
        this.setScrollingTimeout('vertical');
        setTransform({
          el: verticalBar,
          v: 'Y',
          offset: this.verticalOffset
        });
      }
      const horizontalSize = (clientWidth / scrollWidth * this.container.clientWidth - BAR_OFFSET * +(this.verticalProportion < 1)) * horizontalBarProportion;
      this.setState({
        // при большом кол-ве эл-тов каретка может стать слишком маленькой,
        // поэтому у нее есть минимальный размер (PFP-548)
        horizontalSize: Math.max(horizontalSize, this.barMinHeight),
        verticalSize,
        horizontalShow: this.horizontalProportion < 1,
        verticalShow: this.verticalProportion < 1
      }, this.onStateUpdate);
      onScroll == null || onScroll(event, {
        scrollTop,
        scrollLeft,
        scrollWidth,
        scrollHeight,
        clientWidth,
        clientHeight
      });
      this.updateFade({
        scrollTop,
        scrollHeight,
        clientHeight
      });
    };
    this.handleMouseOver = () => {
      if (!this.state.hovered) {
        this.setState({
          hovered: true
        });
      }
    };
    this.handleMouseLeave = () => {
      if (this.state.hovered) {
        this.setState({
          hovered: false
        });
      }
    };
    this.handleMouseDown = (event, direction) => {
      this.direction = direction;
      this.setDraggingDirection();
    };
    this.handleWheel = () => {
      if (!this.state.scrolling) {
        this.setState({
          scrolling: true
        });
      }
      clearTimeout(this.scrollingId);
      this.scrollingId = window.setTimeout(() => {
        this.setState({
          scrolling: false
        });
      }, FADE_TIMEOUT);
    };
    this.handleNativeContainerScroll = event => {
      var _this$props$onScroll, _this$props2;
      const {
        scrollTop,
        scrollLeft,
        scrollWidth,
        scrollHeight,
        clientWidth,
        clientHeight
      } = event.currentTarget;
      (_this$props$onScroll = (_this$props2 = this.props).onScroll) == null || _this$props$onScroll.call(_this$props2, event, {
        scrollTop,
        scrollLeft,
        scrollWidth,
        scrollHeight,
        clientWidth,
        clientHeight
      });
      this.updateFade({
        scrollTop,
        scrollHeight,
        clientHeight
      });
    };
    this.renderScrollBar = (direction, props) => {
      return this.state[`${direction}Show`] && /*#__PURE__*/React.createElement(ScrollBar, Object.assign({
        scrollbarMargin: this.props.scrollbarMargin,
        direction: direction,
        visible: this.getScrollBarVisibility(direction),
        dragging: this.state[direction === 'vertical' ? 'isVDragging' : 'isHDragging'],
        size: this.state[`${direction}Size`],
        theme: this.props.theme,
        barRef: this.setRef(`${direction}Bar`),
        onMouseDown: this.handleMouseDown,
        handleDragStart: this.onDragStart,
        handleDragProcess: this.onDragProcess,
        handleDragEnd: this.onDragEnd
      }, props));
    };
    this.state = {
      horizontalShow: false,
      horizontalSize: 0,
      hovered: false,
      isVScrolling: false,
      mounted: false,
      verticalOffset: 0,
      verticalShow: false,
      verticalSize: 0
    };
    _props.getUpdateFunc == null || _props.getUpdateFunc(this.updateWithProportions);
    _props.getMouseDownFunc == null || _props.getMouseDownFunc(this.handleMouseDown);
    _props.getDragStartFunc == null || _props.getDragStartFunc(this.onDragStart);
    _props.getDragProcessFunc == null || _props.getDragProcessFunc(this.onDragProcess);
    _props.getDragEndFunc == null || _props.getDragEndFunc(this.onDragEnd);
  }
  componentDidMount() {
    this.isUnmounting = false;
    this.updateWithProportions();
    this.setState({
      mounted: true
    }, () => {
      var _this$content;
      if (this.props.preventOuterScroll) {
        this.setListeners();
      }
      if (typeof ResizeObserver !== 'undefined' && this.container && (_this$content = this.content) != null && _this$content.firstChild) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateWithProportions();
        });
        this.resizeObserver.observe(this.container);
        this.resizeObserver.observe(this.content.firstChild);
        this.mutationObserver = new MutationObserver(() => {
          if (!this.content) return;
          const {
            clientWidth,
            scrollWidth
          } = this.content;
          if (this.horizontalProportion !== clientWidth / scrollWidth) {
            this.updateWithProportions();
          }
        });
        this.mutationObserver.observe(this.content.firstChild, {
          childList: true,
          subtree: true,
          attributes: true
        });
      }
    });
  }
  componentDidUpdate(prevProps) {
    if (this.barMinHeight !== this.props.barMinHeight) {
      this.barMinHeight = setBarMinHeight(this.props.barMinHeight);
    }
    if (this.props.preventOuterScroll && !prevProps.preventOuterScroll) {
      this.setListeners();
    } else if (!this.props.preventOuterScroll && prevProps.preventOuterScroll) {
      this.removeListeners();
    }
  }
  componentWillUnmount() {
    this.isUnmounting = true;
    if (this.props.preventOuterScroll) {
      this.removeListeners();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
  onStateUpdate() {
    var _this$props$onUpdate, _this$props3;
    const {
      verticalSize,
      horizontalSize,
      verticalShow,
      horizontalShow,
      isVScrolling,
      isHScrolling,
      isVDragging,
      isHDragging
    } = this.state;
    (_this$props$onUpdate = (_this$props3 = this.props).onUpdate) == null || _this$props$onUpdate.call(_this$props3, {
      verticalSize,
      horizontalSize,
      verticalShow,
      horizontalShow,
      isVScrolling,
      isHScrolling,
      isVDragging,
      isHDragging
    });
  }
  setScrollingTimeout(direction) {
    const isVertical = direction === 'vertical';
    const timeoutId = isVertical ? 'vTimeoutId' : 'hTimeoutId';
    clearTimeout(this[timeoutId]);
    this[timeoutId] = window.setTimeout(() => {
      if (!this.isUnmounting) {
        this.setState({
          [isVertical ? 'isVScrolling' : 'isHScrolling']: false
        }, this.onStateUpdate);
      }
    }, FADE_TIMEOUT);
  }
  updateWithProportions(props, prevProps) {
    const {
      scrollTop,
      scrollLeft
    } = props || this.props;
    const {
      scrollTop: prevScrollTop,
      scrollLeft: prevScrollLeft
    } = prevProps || {};
    requestAnimationFrame(() => {
      if (!this.content) {
        return;
      }
      const {
        clientWidth,
        clientHeight,
        scrollWidth,
        scrollHeight
      } = this.content;
      this.horizontalProportion = clientWidth / scrollWidth;
      this.verticalProportion = clientHeight / scrollHeight;
      if (scrollTop && scrollTop !== prevScrollTop) {
        this.content.scrollTop = scrollTop;
      }
      if (scrollLeft && scrollLeft !== prevScrollLeft) {
        this.content.scrollLeft = scrollLeft;
      }
      this.handleScroll();
    });
  }
  setDraggingDirection() {
    this.setState({
      isHDragging: /horizontal/.test(this.direction || ''),
      isVDragging: /vertical/.test(this.direction || '')
    }, this.onStateUpdate);
  }
  render() {
    const {
      mounted
    } = this.state;
    const {
      zoom
    } = this.context;
    const {
      id,
      dataQaType,
      contentInnerOffsetTop,
      contentInnerOffsetBottom,
      children,
      disabled,
      preventStackContext,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      fallback,
      styleSlots,
      scrollContain,
      classNames,
      styles,
      UNSAFE_className,
      UNSAFE_style,
      verticalFade
    } = this.props;

    /**
     * Используем нативный скролл только если явно передан соответствующий проп
     */
    const native = this.mobile && fallback === false;
    const onScroll = native ? this.handleNativeContainerScroll : this.handleScroll;
    const renderScrollBars = mounted && !native && !disabled;
    return (
      /*#__PURE__*/
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
      React.createElement("div", Object.assign({}, dataQa(this.props), pickStyle(this.props, 'root', {
        className: cn(UNSAFE_className, {
          [classes.scroll]: true,
          [classes.scroll_preventStackContext]: native || preventStackContext
        }),
        style: UNSAFE_style
      }), {
        ref: this.setRef('container'),
        onMouseOver: this.handleMouseOver,
        onMouseLeave: this.handleMouseLeave,
        "data-vertical-fade": !!verticalFade
      }), /*#__PURE__*/React.createElement(OffsetWrapper, Object.assign({}, pickStyle(this.props, 'offsetWrapper'), {
        offsetBottom: contentInnerOffsetBottom,
        offsetTop: contentInnerOffsetTop
      }), /*#__PURE__*/React.createElement(ScrollContent, Object.assign({}, pickStyle(this.props, 'scrollContent'), {
        id: id,
        dataQaType: dataQaType,
        ref: this.contentRef,
        onScroll: onScroll,
        onTouchStart: onTouchStart,
        onTouchMove: onTouchMove,
        onTouchEnd: onTouchEnd,
        onWheel: this.handleWheel,
        native: native,
        mounted: mounted,
        disabled: disabled,
        scrollContain: scrollContain
      }), /*#__PURE__*/React.createElement(PortalWrapper, {
        classNames: extractLayer(classNames, 'portalWrapper'),
        styles: extractLayer(styles, 'portalWrapper'),
        styleSlots: styleSlots == null ? void 0 : styleSlots.portalWrapper
      }, /*#__PURE__*/React.createElement(PopoverContext.Provider, {
        value: this.container ? {
          intersectElems: [this.container],
          zoom
        } : {}
      }, children)))), renderScrollBars && /*#__PURE__*/React.createElement(React.Fragment, null, this.renderScrollBar('horizontal', {
        classNames: extractLayer(classNames, 'scrollBarHorizontal'),
        styles: extractLayer(styles, 'scrollBarHorizontal'),
        styleSlots: styleSlots == null ? void 0 : styleSlots.scrollBarHorizontal
      }), this.renderScrollBar('vertical', {
        classNames: extractLayer(classNames, 'scrollBarVertical'),
        styles: extractLayer(styles, 'scrollBarVertical'),
        styleSlots: styleSlots == null ? void 0 : styleSlots.scrollBarVertical
      })))
    );
  }
}
Scroll.contextType = PopoverContext;
Scroll.defaultProps = {
  theme: 'default',
  preventOuterScroll: false,
  scrollbarVisibility: SCROLLBAR_VISIBILITY.ONLY_ACTIVE,
  preventStackContext: false,
  dataQaType: 'uikit/scroll',
  contentInnerOffsetBottom: 0,
  contentInnerOffsetTop: 0,
  barMinHeight: 0
};
export default Scroll;